import React, { Fragment, useContext, useMemo } from 'react';
import {
  ascending,
  bisect,
  extent,
  merge,
  quantileSorted,
  sum,
} from 'd3-array';
import { timeDay, timeHour, timeMinute, timeYear } from 'd3-time';

import BaseSVG, { zeroMargins } from './BaseSVG';
import { getRanges  } from './GlucoseStatsRow';
import MainView from './MainView';
import PresentationContext from '../PresentationContext';
import TargetContext from './TargetContext';
import YAxis from './YAxis';
import { dayFormat } from '../reducers/data';
import { mgdL, internalUnits, consts } from '../utils/consts';
import { glucoseAccessor, nameKey } from '../utils/dataUtils';
import { parseDate } from '../utils/formats';
import './Dashboard.css';

const debug = (process.env.NODE_ENV !== 'production') && true;

const dayPadding = 0.2;
const weekPadding = 5;

function Cycles(props) {

  const { targets } = useContext(TargetContext);

  const {
    formatDate,
    weekDays,
    weekInterval,
    yearAndWeekNumberFormat,
  } = useContext(PresentationContext);

  // If no periods are tracked, assume 28 day periods from the start of the millennium.
  // TODO: think about how to visualize the data if someone has just started tracking periods,
  // or tracked in the past but not recently.
  const defaultPeriods = weekInterval.range(new Date(2000, 0, 1), new Date(), 4);

  const {
    days = [],
    bolusData = {},
    basalData = {},
    exerciseData = {},
    formatDateRange,
    formatGlucoseValue,
    interactive = true,
    longActingData = {},
    nutritionData = {},
    percentile = 90,
    periods = defaultPeriods,
    sgvData = {},
    sleepData = {},
    stepsData = {},
    t,
  } = props;

  if (debug) {
    console.log('Cycles data', props);
  }


  // Only check the sleep from previous day?
  // At least ignore any sleep that carries over to the next day? 
  const dailySleepData = Object.values(sleepData).reduce((o, s) => {
    s.forEach((e) => {
      const day = dayFormat(e.endTime ? new Date(e.endTime) : timeMinute.offset(new Date(e.time, e.duration)));
      if (!o[day]) {
        o[day] = [e];
      } else {
        const i = bisect(Object.keys(o), e.endTime);
        o[day].splice(i, 0, e);
      }
      console.log('Formatting date', { e, endTime: e.endTime, day, formatted: dayFormat(new Date(e.endTime)) }, o);
    });
    return o;
  }, {});
 
  const defaultTicks = consts[internalUnits].ticks;

  const ticks = useMemo(() => ({
    ...defaultTicks,
    high: targets.top,
    low: targets.bottom,
  }), [targets]);


  // Todo: calculate from data
  // If there are clear outliers, split them in two?
  const maxPeriodLength = 28;

  const binThresholds = useMemo(() => {
    const thresholds = Object.values(ticks).sort(ascending);
    // Replace max value to include all possible values
    thresholds[thresholds.length - 1] = internalUnits === 'mmol/l' ? 50 : 1000;
    return thresholds;
  }, [ticks]);

  const dateBins = useMemo(() => {
    // console.log('Reducing', p);
    const dates = periods.map(d => new Date(d));
    const years = dates.reduce((o, d, i) => {
      const year = d.getFullYear();
      if (!o[year]) {
        o[year] = [];
      }
      o[year].push(d);
      return o;
    }, {});
    const bins = Object.keys(years).reduce((o, y) => {
      console.log('Constructing years', { o, y, years })
      years[y].forEach((p, i, a) => {
        const nextDate = (i < (a.length - 1)) ? a[i + 1] : (years[ +y + 1 ] ? years[ +y + 1 ][0] : timeDay.offset(p, 28));
        const endDate = timeDay.offset(nextDate, -1);
        const periodDays = {
          key: yearAndWeekNumberFormat(timeDay.offset(p, 6)),
          days: `${p.toDateString()} - ${endDate.toDateString()}`,
          data: [],
        };
        let foundData = false;
        const periodLength = timeDay.count(p, nextDate);
        for (let j = 0; j < periodLength; j += 1) {
          const date = timeDay.offset(p, j);
          if (!periodDays.data[j]) {
            periodDays.data[j] = {
              date: `${weekDays[timeDay.count(weekInterval(date), date)]} ${formatDate(date)}`,
            };
          }
          const key = dayFormat(date);
          if (y === '2020') {
            console.log('Searching for data', { date, key }, periodDays.data[j]);
          }
          const sgv = sgvData[key];
          if (sgv) {
            // const sortedSgv = sgv.map(glucoseAccessor).sort(ascending);
            periodDays.data[j].tir = getRanges(sgv, binThresholds).target;
            foundData = true;
          }
          const basal = basalData[key];
          if (basal) {
            periodDays.data[j].basal = sum(basal, d => d.amount);
            foundData = true;
          }
          const bolus = bolusData[key];
          if (bolus) {
            periodDays.data[j].bolus = sum(bolus, d => d.amount);
            foundData = true;
          }
          if (periodDays.data[j].basal || periodDays.data[j].bolus) {
            periodDays.data[j].tdd = (periodDays.data[j].basal || 0) + (periodDays.data[j].bolus || 0);
            foundData = true;
          }
          const carbs = nutritionData[key];
          if (carbs) {
            periodDays.data[j].carbs = sum(carbs, d => d.carbohydrate);
            foundData = true;
          }
          const exercises = exerciseData[key];
          if (exercises) {
            periodDays.data[j].exercise = sum(exercises, d => d.duration);
            foundData = true;
          }
          const sleep = dailySleepData[key];
          if (sleep) {
            periodDays.data[j].sleep = sum(sleep, d => d.duration);
            console.log('Found sleep data', sleep, sum(sleep, d => d.duration));
            foundData = true;
          }
          const steps = stepsData[key];
          if (steps) {
            periodDays.data[j].steps = sum(steps, d => d.steps);
            foundData = true;
          }
        }
        if (foundData) {
          console.log('Found data!', periodDays);
          if (!o[y]) {
            o[y] = [];
          }
          o[y].push(periodDays);
          // console.log('Found data!', p, a);
        } else {
          console.log('No data', { p, periodDays });
        }
      });
      return o;
    }, {});
    console.log('Processed years', years, bins);
    return bins;
  }, [periods, basalData, bolusData, exerciseData, nutritionData, sleepData, stepsData, sgvData]);

  console.log('Data', dateBins, { dailySleepData, stepsData, exerciseData });

  const periodLength = 28;
  const dayWidthMax = (720 - ((periodLength - 1) * dayPadding) - ((Math.ceil(periodLength / 7) + 1) * weekPadding)) / periodLength;
  const dayWidth = Math.min(5, dayWidthMax);
  const periodPadding = 6;
  const periodHeight = dayWidth + dayPadding;

  const stats = useMemo(() => {
    console.log('Counting stats', dateBins);
    const data = merge(Object.values(dateBins)).reduce((a, d) => [...a, ...d.data], []);

    return ['basal', 'bolus', 'carbs', 'exercise', 'sleep', 'steps', 'tdd', 'tir'].reduce((o, k) => {
      const values = data.map(d => d[k]).filter(d => !!d).sort(ascending);
      if (values.length) {
        o[k].extent = extent(values);
        o[k].quantiles = [quantileSorted(values, 1/3), quantileSorted(values, 2/3)];
      }
      return o;
    }, {
      basal: { extent: [undefined, undefined], quantiles: [undefined, undefined] },
      bolus: { extent: [undefined, undefined], quantiles: [undefined, undefined] },
      carbs: { extent: [undefined, undefined], quantiles: [undefined, undefined] },
      exercise: { extent: [undefined, undefined], quantiles: [undefined, undefined] },
      sleep: { extent: [undefined, undefined], quantiles: [undefined, undefined] },
      steps: { extent: [undefined, undefined], quantiles: [undefined, undefined] },
      tdd: { extent: [undefined, undefined], quantiles: [undefined, undefined] },
      tir: { extent: [undefined, undefined], quantiles: [undefined, undefined] },
    });
  }, [dateBins]);

  console.log({ periods, sgvData, dateBins, stats });

  function getClass(value, quantiles) {
    if (value <= quantiles[0]) {
      return 'low';
    }
    if (value >= quantiles[1]) {
      return 'high';
    }
    return 'average';
  }

  // get the highest of steps / exercise
  function getActivityClass(exercise, steps) {
    const exerciseClass = getClass(exercise, stats.exercise.quantiles);
    const stepsClass = getClass(steps, stats.steps.quantiles);
    if (exerciseClass === 'high' || stepsClass === 'high') {
      return 'high';
    }
    if (exerciseClass === 'average' || stepsClass === 'average') {
      return 'average';
    }
    if (exerciseClass === 'low' || stepsClass === 'low') {
      return 'low';
    }
    return exerciseClass || stepsClass;
  }

  const w = dayWidth;
  const r = w / 5;
  const s = w - (r * 2);
  const hs = s / 2;
  const periodWidth = ((dayWidth + dayPadding) * maxPeriodLength) + (weekPadding * Math.ceil(maxPeriodLength / 7));
  const svgHeight = (periodHeight * periods.length) + (periodPadding * Object.keys(dateBins).length);

  const selectedVariable = 'carbs';

  let yearY = 0;

  return (
    <MainView
      description={t('cycles.main.description')}
      heading={t('cycles.main.heading')}
      interactive={interactive}
      key="mainView"
      title={`Sensotrend Cycles Report ${formatDateRange(days)}`}
    >
      <section id="cycles">
      <BaseSVG
              showXAxis={false}
              key="mainGraph"
              width={periodWidth}
              height={svgHeight}
              svgWidth={periodWidth}
              svgHeight={svgHeight}
              margins={{
                top: 0,
                right: 0,
                bottom: 0,
                left: 60,
              }}
              style={{
                paddingBottom: 'auto',
                width: 'auto',
                height: 'auto',
              }}
            >

        {Object.keys(dateBins).map((y, yi) => (
          <g key={y} className="year" data={y} transform={`translate(0,${(yearY + (periodPadding * yi))})`}>
            {dateBins[y].map((pd, pi) => (
                <g key={pd.days} transform={`translate(${weekPadding}, ${pi * periodHeight})`} data={pd.days}>
                  {(yearY += periodHeight) && pd.data.map((day, i) => {
                    // console.log('Rendering', day);
                    return (
                      <g key={`cycles-${pd.days}-day${i}`} shapeRendering="geometricPrecision">
                        <path
                          className={`cycles ${selectedVariable} ${getClass(day[selectedVariable], stats[selectedVariable].quantiles)}`}
                          data={day[selectedVariable]}
                          transform={`translate(${(i * (dayWidth + dayPadding)) + (Math.floor(i / 7) * weekPadding)},0)`}
                          d={`M${w / 2},0 h${hs} a${r},${r} 0 0 1 ${r},${r} v${s} a${r},${r} 0 0 1 ${-r},${r} h${-s} a${r},${r} 0 0 1 ${-r},${-r} v${-s} a${r},${r} 0 0 1 ${r},${-r} z`}
                        >
                          <title>
                            {`${day.date}: ${selectedVariable} ${day[selectedVariable]}`}
                          </title>
                        </path>

                        {/*
                        <path
                          className={`cycles tir ${getClass(day.tir, stats.tir.quantiles)}`}
                          data={day.tir}
                          transform={`translate(${(i * (dayWidth + dayPadding)) + (Math.floor(i / 7) * weekPadding)},0)`}
                          d={`M${w / 2},0 h${hs} a${r},${r} 0 0 1 ${r},${r} v${s} a${r},${r} 0 0 1 ${-r},${r} h${-s} a${r},${r} 0 0 1 ${-r},${-r} v${-s} a${r},${r} 0 0 1 ${r},${-r} z`}
                        >
                          <title>
                            {`${day.date}: Time in Range: ${day.tir}`}
                          </title>
                        </path>
                        
                        <path
                          className={`cycles carbs ${getClass(day.carbs, stats.carbs.quantiles)}`}
                          data={day.carbs}
                          transform={`translate(${(i * (dayWidth + dayPadding)) + (Math.floor(i / 7) * weekPadding)},${dayWidth + dayPadding})`}
                          d={`M${w / 2},0 h${hs} a${r},${r} 0 0 1 ${r},${r} v${s} a${r},${r} 0 0 1 ${-r},${r} h${-s} a${r},${r} 0 0 1 ${-r},${-r} v${-s} a${r},${r} 0 0 1 ${r},${-r} z`}
                        />

                        <path
                          className={`cycles tdd ${getClass(day.tdd, stats.tdd.quantiles)}`}
                          data={day.tdd}
                          transform={`translate(${(i * (dayWidth + dayPadding)) + (Math.floor(i / 7) * weekPadding)},${(dayWidth + dayPadding) * 2})`}
                          d={`M${w / 2},0 h${hs} a${r},${r} 0 0 1 ${r},${r} v${s} a${r},${r} 0 0 1 ${-r},${r} h${-s} a${r},${r} 0 0 1 ${-r},${-r} v${-s} a${r},${r} 0 0 1 ${r},${-r} z`}
                        />
                        <path
                          className={`cycles activity ${getActivityClass(day.exercise, day.steps)}`}
                          data={day.tdd}
                          transform={`translate(${(i * (dayWidth + dayPadding)) + (Math.floor(i / 7) * weekPadding)},${(dayWidth + dayPadding) * 3})`}
                          d={`M${w / 2},0 h${hs} a${r},${r} 0 0 1 ${r},${r} v${s} a${r},${r} 0 0 1 ${-r},${r} h${-s} a${r},${r} 0 0 1 ${-r},${-r} v${-s} a${r},${r} 0 0 1 ${r},${-r} z`}
                        >
                          <title>
                            {`${day.date}: ${day.steps || 0} steps, ${day.exercise || 0} min of excercise.`}
                          </title>
                        </path>
                        <path
                          className={`cycles sleep ${getClass(day.sleep, stats.sleep.quantiles)}`}
                          data={day.tdd}
                          transform={`translate(${(i * (dayWidth + dayPadding)) + (Math.floor(i / 7) * weekPadding)},${(dayWidth + dayPadding) * 4})`}
                          d={`M${w / 2},0 h${hs} a${r},${r} 0 0 1 ${r},${r} v${s} a${r},${r} 0 0 1 ${-r},${r} h${-s} a${r},${r} 0 0 1 ${-r},${-r} v${-s} a${r},${r} 0 0 1 ${r},${-r} z`}
                        >
                          <title>
                            {`${day.date}: ${day.sleep} min of sleep.`}
                          </title>
                        </path>
                        */}

                      </g>
                    );
                  })}
                </g>
              ))}
          </g>
        ))}
        </BaseSVG>
      </section>
      <section>
        <BaseSVG height={0} svgWidth={0} svgHeight={0} margins={zeroMargins} showXAxis={false} key="firstAdditionalGraph" />
      </section>
      <section>
        <BaseSVG height={0} svgWidth={0} svgHeight={0} margins={zeroMargins} showXAxis={false} key="secondAdditionalGraph" />
      </section>
    </MainView>
  );
}

export default Cycles;
